/**
 * Persisted WMS module ordinals. These values are stored in roles and JWTs,
 * so consumers must never reorder or renumber them.
 */
export const Module = Object.freeze({
  StockIn: 0,
  StockOut: 1,
  StockTransfer: 2,
  StockReservation: 3,
  Category: 4,
  Colour: 5,
  Design: 6,
  Location: 7,
  Product: 8,
  Inventory: 9,
  Size: 10,
  StockGroup: 11,
  Warehouse: 12,
  User: 13,
  UserRole: 14,
  ClientCode: 15,
  StockAdjustment: 16,
  LogisticsDelivery: 17,
  Driver: 18,
  Vehicle: 19,
  LogisticsAssignment: 20,
  SQLBISync: 21,
} as const);

export type ModuleId = (typeof Module)[keyof typeof Module];

export const InventoryPermission = Object.freeze({
  Dashboard: "inventory.dashboard",
  Movements: "inventory.movements",
  ByLocation: "inventory.by-location",
  ByProduct: "inventory.by-product",
  DateRange: "inventory.date-range",
} as const);

export type InventoryPermissionId =
  (typeof InventoryPermission)[keyof typeof InventoryPermission];

export const StockInPermission = Object.freeze({
  BulkUpload: "stock-in.bulk-upload",
  Export: "stock-in.export",
} as const);

export type StockInPermissionId =
  (typeof StockInPermission)[keyof typeof StockInPermission];

export const StockOutPermission = Object.freeze({
  Export: "stock-out.export",
} as const);

export type StockOutPermissionId =
  (typeof StockOutPermission)[keyof typeof StockOutPermission];

export const ReservationPermission = Object.freeze({
  Cancel: "reservation.cancel",
  Release: "reservation.release",
} as const);
export type ReservationPermissionId =
  (typeof ReservationPermission)[keyof typeof ReservationPermission];
export const RESERVATION_PERMISSION_LABELS: Readonly<
  Record<ReservationPermissionId, string>
> = Object.freeze({
  [ReservationPermission.Cancel]: "Cancel reservation",
  [ReservationPermission.Release]: "Release to Stock Out",
});

export type PermissionId =
  | InventoryPermissionId
  | StockInPermissionId
  | StockOutPermissionId
  | ReservationPermissionId;

export const INVENTORY_PERMISSION_LABELS: Readonly<
  Record<InventoryPermissionId, string>
> = Object.freeze({
  [InventoryPermission.Dashboard]: "Dashboard",
  [InventoryPermission.Movements]: "Movements",
  [InventoryPermission.ByLocation]: "By location",
  [InventoryPermission.ByProduct]: "By product",
  [InventoryPermission.DateRange]: "Date range",
});

export const STOCK_IN_PERMISSION_LABELS: Readonly<
  Record<StockInPermissionId, string>
> = Object.freeze({
  [StockInPermission.BulkUpload]: "Bulk upload",
  [StockInPermission.Export]: "Export to Excel",
});

export const STOCK_OUT_PERMISSION_LABELS: Readonly<
  Record<StockOutPermissionId, string>
> = Object.freeze({
  [StockOutPermission.Export]: "Export to Excel",
});

export const MODULE_LABELS: Readonly<Record<ModuleId, string>> = Object.freeze({
  [Module.StockIn]: "Stock In",
  [Module.StockOut]: "Stock Out",
  [Module.StockTransfer]: "Stock Transfer",
  [Module.StockReservation]: "Stock Reservation",
  [Module.Category]: "Category",
  [Module.Colour]: "Colour",
  [Module.Design]: "Design",
  [Module.Location]: "Location",
  [Module.Product]: "Product",
  [Module.Inventory]: "Inventory",
  [Module.Size]: "Size",
  [Module.StockGroup]: "Stock Group",
  [Module.Warehouse]: "Warehouse",
  [Module.User]: "User",
  [Module.UserRole]: "User Role",
  [Module.ClientCode]: "Client Code",
  [Module.StockAdjustment]: "Stock Adjustment",
  [Module.LogisticsDelivery]: "Logistics Delivery",
  [Module.Driver]: "Driver",
  [Module.Vehicle]: "Vehicle",
  [Module.LogisticsAssignment]: "Logistics Assignment",
  [Module.SQLBISync]: "SQL BI Sync",
});
